import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Shapes, SlidersHorizontal } from 'lucide-react';
import {
  ExtendPracticeDialog,
  FreePracticeDialog,
  RecapPracticeDialog,
} from '@/components/create-practice-dialog';
import {
  BigCardDecor,
  FreeCardDecor,
  RecapCardDecor,
} from '@/components/practice-decorations';
import MobileLandscapeDashboardShell, { useStageFit } from './mobile-landscape-dashboard-shell';

// 手机横屏(844×390)隨心練页主体:三张彩色卡片(更多練習/錯題重練/自由主題),配色走 CSS 变量、装饰复用 practice-decorations、点击复用桌面 create-practice-dialog

type Pos = { left: number; top: number; width: number; height: number };

interface StageCardProps {
  pos: Pos;
  bg: string;
  fg: string;
  icon: ReactNode;
  title: string;
  desc: string;
  decor: ReactNode;
  descMaxWidth: number;
  padTop: number;
  padLeft: number;
  titleSize: number;
  titleMt: number;
  descSize: number;
  descMt: number;
  onClick: () => void;
  isLoading?: boolean;
}

function StageCard({
  pos, bg, fg, icon, title, desc, decor, descMaxWidth,
  padTop, padLeft, titleSize, titleMt, descSize, descMt, onClick, isLoading,
}: StageCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      style={{
        position: 'absolute', left: pos.left, top: pos.top, width: pos.width, height: pos.height,
        borderRadius: 20, background: bg, color: fg, overflow: 'hidden',
        border: 0, cursor: 'pointer', textAlign: 'left', padding: 0,
      }}
      className="flex flex-col items-start shadow-md transition-transform duration-300 hover:scale-[1.01] active:scale-[0.99] disabled:pointer-events-none disabled:opacity-75"
    >
      {decor}
      <div style={{ position: 'relative', zIndex: 10, paddingTop: padTop, paddingLeft: padLeft, paddingRight: 16 }}>
        {/* lineHeight:0 让图标块高恰等于图标本身高(18)，titleMt 才能按设计精确落位 */}
        <div style={{ lineHeight: 0 }}>{icon}</div>
        <div style={{ marginTop: titleMt, fontSize: titleSize, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.01em', fontFamily: 'var(--font-sans)' }}>
          {title}
        </div>
        <div style={{ marginTop: descMt, fontSize: descSize, lineHeight: 1.2, opacity: 0.82, maxWidth: descMaxWidth, fontFamily: 'var(--font-sans)' }}>
          {desc}
        </div>
      </div>
    </button>
  );
}

// 仅手机横屏(高≤500)挂载,桌面/平板返回 null,避免 FreePracticeDialog 等弹窗在桌面额外触发 getTopicList/getKnowledgePointList/getSchoolPress,保证桌面逻辑与请求不变
function useMobileLandscape() {
  const [is, setIs] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-height: 500px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-height: 500px)');
    const on = () => setIs(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return is;
}

export default function MobileLandscapePractice() {
  const { t } = useTranslation();
  const isMobileLandscape = useMobileLandscape();
  // 宽屏铺满:取与舞台壳一致的 shift(见 useStageFit)。大卡吃掉 shift 变宽,右列 recap/free 整体右移贴右。
  const { shift } = useStageFit();

  if (!isMobileLandscape) return null;

  const content = (
    <>
      {/* 页面标题(与學習概覽同 h1 尺寸/坐标,保持 dashboard 各页一致) */}
      <h1 style={{ position: 'absolute', left: 204, top: 68, margin: 0, fontSize: 20, fontWeight: 700, color: '#000', fontFamily: 'var(--font-sans)', lineHeight: '24px' }}>
        {t('student.practice')}
      </h1>

      {/* ── 大卡：更多練習(左,撑满卡区高) ── */}
      <ExtendPracticeDialog
        renderTrigger={({ onClick, isLoading }) => (
          <StageCard
            pos={{ left: 208, top: 98, width: 306 + shift, height: 280 }}
            bg="var(--practice-big-card)"
            fg="var(--practice-big-card-fg)"
            icon={<Shapes strokeWidth={1.8} style={{ width: 18, height: 18, color: 'var(--practice-big-card-fg)' }} />}
            title={t('component.exttitle')}
            desc={t('component.extdesc')}
            decor={<BigCardDecor />}
            descMaxWidth={158}
            padTop={18} padLeft={25} titleSize={16} titleMt={9} descSize={12} descMt={11}
            onClick={onClick}
            isLoading={isLoading}
          />
        )}
      />

      {/* ── Recap 卡：錯題重練(右上) ── */}
      <RecapPracticeDialog
        renderTrigger={({ onClick, isLoading }) => (
          <StageCard
            pos={{ left: 524 + shift, top: 98, width: 309, height: 136 }}
            bg="var(--practice-recap-card)"
            fg="var(--practice-recap-card-fg)"
            icon={<CircleAlert strokeWidth={1.8} color="#BA1A1A" style={{ width: 18, height: 18 }} />}
            title={t('component.recaptitle')}
            desc={t('component.recapdesc')}
            decor={<RecapCardDecor />}
            descMaxWidth={162}
            padTop={18} padLeft={27} titleSize={16} titleMt={9} descSize={12} descMt={11}
            onClick={onClick}
            isLoading={isLoading}
          />
        )}
      />

      {/* ── Free 卡：自由主題(右下) ── */}
      <FreePracticeDialog
        renderTrigger={({ onClick, isLoading }) => (
          <StageCard
            pos={{ left: 524 + shift, top: 242, width: 309, height: 136 }}
            bg="var(--practice-free-card)"
            fg="var(--practice-free-card-fg)"
            icon={<SlidersHorizontal strokeWidth={1.8} style={{ width: 18, height: 18, color: 'var(--practice-free-card-fg)' }} />}
            title={t('component.freetitle')}
            desc={t('component.freedesc')}
            decor={<FreeCardDecor />}
            descMaxWidth={147}
            padTop={22} padLeft={27} titleSize={16} titleMt={10} descSize={12} descMt={11}
            onClick={onClick}
            isLoading={isLoading}
          />
        )}
      />
    </>
  );

  return createPortal(
    <div
      className="hidden [@media(max-height:500px)]:flex fixed inset-0 items-center justify-center overflow-hidden"
      style={{ background: 'var(--practice-page-bg)' }}
    >
      <MobileLandscapeDashboardShell>{content}</MobileLandscapeDashboardShell>
    </div>,
    document.body,
  );
}
